function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

import { arrow as arrow$1, computePosition } from '@floating-ui/dom';
export { autoPlacement, autoUpdate, computePosition, detectOverflow, flip, getOverflowAncestors, hide, inline, limitShift, offset, platform, shift, size } from '@floating-ui/dom';
import { isNode, getNodeName } from '@floating-ui/utils/dom';
import { unref, computed, ref, shallowRef, watch, getCurrentScope, onScopeDispose, shallowReadonly } from 'vue-demi';

function isComponentPublicInstance(target) {
  return target != null && _typeof(target) === 'object' && '$el' in target;
}

function unwrapElement(target) {
  if (isComponentPublicInstance(target)) {
    var element = target.$el;
    return isNode(element) && getNodeName(element) === '#comment' ? null : element;
  }

  return target;
}

function toValue(source) {
  return typeof source === 'function' ? source() : unref(source);
}
/**
 * Positions an inner element of the floating element such that it is centered to the reference element.
 * @param options The arrow options.
 * @see https://floating-ui.com/docs/arrow
 */


function arrow(options) {
  return {
    name: 'arrow',
    options: options,
    fn: function fn(args) {
      var element = unwrapElement(toValue(options.element));

      if (element == null) {
        return {};
      }

      return arrow$1({
        element: element,
        padding: options.padding
      }).fn(args);
    }
  };
}

function getDPR(element) {
  if (typeof window === 'undefined') {
    return 1;
  }

  var win = element.ownerDocument.defaultView || window;
  return win.devicePixelRatio || 1;
}

function roundByDPR(element, value) {
  var dpr = getDPR(element);
  return Math.round(value * dpr) / dpr;
}
/**
 * Computes the `x` and `y` coordinates that will place the floating element next to a reference element when it is given a certain CSS positioning strategy.
 * @param reference The reference template ref.
 * @param floating The floating template ref.
 * @param options The floating options.
 * @see https://floating-ui.com/docs/vue
 */


function useFloating(reference, floating, options) {
  if (options === void 0) {
    options = {};
  }

  var whileElementsMountedOption = options.whileElementsMounted;
  var openOption = computed(function () {
    var _toValue;

    return (_toValue = toValue(options.open)) != null ? _toValue : true;
  });
  var middlewareOption = computed(function () {
    return toValue(options.middleware);
  });
  var placementOption = computed(function () {
    var _toValue2;

    return (_toValue2 = toValue(options.placement)) != null ? _toValue2 : 'bottom';
  });
  var strategyOption = computed(function () {
    var _toValue3;

    return (_toValue3 = toValue(options.strategy)) != null ? _toValue3 : 'absolute';
  });
  var transformOption = computed(function () {
    var _toValue4;

    return (_toValue4 = toValue(options.transform)) != null ? _toValue4 : true;
  });
  var referenceElement = computed(function () {
    return unwrapElement(reference.value);
  });
  var floatingElement = computed(function () {
    return unwrapElement(floating.value);
  });
  var x = ref(0);
  var y = ref(0);
  var strategy = ref(strategyOption.value);
  var placement = ref(placementOption.value);
  var middlewareData = shallowRef({});
  var isPositioned = ref(false);
  var floatingStyles = computed(function () {
    var initialStyles = {
      position: strategy.value,
      left: '0',
      top: '0'
    };

    if (!floatingElement.value) {
      return initialStyles;
    }

    var xVal = roundByDPR(floatingElement.value, x.value);
    var yVal = roundByDPR(floatingElement.value, y.value);

    if (transformOption.value) {
      return _objectSpread(_objectSpread({}, initialStyles), {}, {
        transform: "translate(" + xVal + "px, " + yVal + "px)"
      }, getDPR(floatingElement.value) >= 1.5 && {
        willChange: 'transform'
      });
    }

    return {
      position: strategy.value,
      left: xVal + "px",
      top: yVal + "px"
    };
  });
  var whileElementsMountedCleanup;

  function update() {
    if (referenceElement.value == null || floatingElement.value == null) {
      return;
    }

    var open = openOption.value;
    computePosition(referenceElement.value, floatingElement.value, {
      middleware: middlewareOption.value,
      placement: placementOption.value,
      strategy: strategyOption.value
    }).then(function (position) {
      x.value = position.x;
      y.value = position.y;
      strategy.value = position.strategy;
      placement.value = position.placement;
      middlewareData.value = position.middlewareData;
      /**
       * The floating element's position may be recomputed while it's closed
       * but still mounted (such as when transitioning out). To ensure
       * `isPositioned` will be `false` initially on the next open, avoid
       * setting it to `true` when `open === false` (must be specified).
       */

      isPositioned.value = open !== false;
    });
  }

  function cleanup() {
    if (typeof whileElementsMountedCleanup === 'function') {
      whileElementsMountedCleanup();
      whileElementsMountedCleanup = undefined;
    }
  }

  function attach() {
    cleanup();

    if (whileElementsMountedOption === undefined) {
      update();
      return;
    }

    if (referenceElement.value != null && floatingElement.value != null) {
      whileElementsMountedCleanup = whileElementsMountedOption(referenceElement.value, floatingElement.value, update);
      return;
    }
  }

  function reset() {
    if (!openOption.value) {
      isPositioned.value = false;
    }
  }

  watch([middlewareOption, placementOption, strategyOption, openOption], update, {
    flush: 'sync'
  });
  watch([referenceElement, floatingElement], attach, {
    flush: 'sync'
  });
  watch(openOption, reset, {
    flush: 'sync'
  });

  if (getCurrentScope()) {
    onScopeDispose(cleanup);
  }

  return {
    x: shallowReadonly(x),
    y: shallowReadonly(y),
    strategy: shallowReadonly(strategy),
    placement: shallowReadonly(placement),
    middlewareData: shallowReadonly(middlewareData),
    isPositioned: shallowReadonly(isPositioned),
    floatingStyles: floatingStyles,
    update: update
  };
}

export { arrow, useFloating };